import React from 'react';
import { useSchemaOrg } from '@unhead/schema-org/react';

const APARTMENT_SLUGS = new Set([
    'apartment',
    'flat',
    'penthouse',
    'studio',
    'maisonette',
]);

/** @type {Record<string, string>} */
const RENT_PERIOD_LABELS = {
    WEEKLY: 'per week',
    MONTHLY: 'per month',
    ANNUALLY: 'per year',
};

/** @type {Record<string, string>} */
const AVAILABILITY_MAP = {
    CURRENT: 'https://schema.org/InStock',
    SOLD: 'https://schema.org/OutOfStock',
    LEASED: 'https://schema.org/OutOfStock',
    WITHDRAWN: 'https://schema.org/Discontinued',
};

/**
 * Resolves the schema.org type from the property types array.
 * @param {Array<{ name?: string; slug?: string }>} [types]
 * @returns {string}
 */
const resolveSchemaType = (types) => {
    if (!types?.length) {
        return 'House';
    }
    const isApartment = types.some(type => APARTMENT_SLUGS.has(type.slug?.toLowerCase()));
    return isApartment ? 'Apartment' : 'House';
};

/**
 * @type {import('$types/components/partials/properties').PropertySchema}
 */
const PropertySchema = React.memo(({ page, url, isRental = false }) => {
    const images = page?.images
        ?.map(item => item.media?.image?.src)
        .filter(Boolean);

    const schemaType = resolveSchemaType(page?.types);

    const additionalProperties = [
        {
            name: 'EPC Rating',
            value: isRental ? page?.rentalEpcRating : page?.epcRating,
        },
        {
            name: 'Council Tax Band',
            value: page?.councilTaxBand,
        },
        {
            name: 'Council Tax Authority',
            value: page?.councilTaxAuthority,
        },
        {
            name: 'Tenure',
            value: page?.tenure?.name,
        },
        {
            name: 'Tenure Expiry Year',
            value: page?.tenureExpiryYear,
        },
        {
            name: 'Garages',
            value: page?.garages,
        },
        {
            name: 'Outdoor Area Meters',
            value: page?.outdoorAreaMeters,
        },
        ...(isRental ? [
            {
                name: 'Minimum Term Months',
                value: page?.minimumTermMonths,
            },
            {
                name: 'Available From',
                value: page?.availableFromDate,
            },
        ] : []),
    ]
        .filter(prop => prop.value !== undefined && prop.value !== null && prop.value !== '' && prop.value !== 0)
        .map(prop => ({
            '@type': 'PropertyValue',
            'name': prop.name,
            'value': prop.value,
        }));

    const status = isRental ? page?.rentalStatus : page?.status;
    const availability = AVAILABILITY_MAP[status] ?? 'https://schema.org/InStock';

    const offers = isRental
        ? {
            '@type': 'Offer',
            priceCurrency: page?.priceCurrency,
            availability,
            priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: page?.price,
                priceCurrency: page?.priceCurrency,
                unitText: RENT_PERIOD_LABELS[page?.priceRentPeriod] || 'per month',
            },
        }
        : {
            '@type': 'Offer',
            price: page?.price,
            priceCurrency: page?.priceCurrency,
            availability,
        };

    const schema = {
        '@type': schemaType,
        '@id': url,
        name: page?.title,
        description: page?.description || page?.title,
        url,
        image: images,
        numberOfBedrooms: page?.bedrooms,
        numberOfBathroomsTotal: page?.bathrooms,
        ...(page?.livingAreas && { numberOfRooms: page.livingAreas }),
        ...(page?.livingAreaMeters && {
            floorSize: {
                '@type': 'QuantitativeValue',
                value: page.livingAreaMeters,
                unitCode: 'MTK',
            },
        }),
        ...(page?.coordinates?.lat && page?.coordinates?.lng && {
            geo: {
                '@type': 'GeoCoordinates',
                latitude: page.coordinates.lat,
                longitude: page.coordinates.lng,
            },
        }),
        ...(page?.displayAddress && {
            address: {
                '@type': 'PostalAddress',
                streetAddress: page.displayAddress,
            },
        }),
        ...(additionalProperties.length > 0 && {
            additionalProperty: additionalProperties,
        }),
        offers,
    };

    useSchemaOrg(page ? [schema] : [], { tagPosition: 'head' });

    return null;
});

PropertySchema.displayName = 'PropertySchema';

export default PropertySchema;
